import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
  catchError,
  EMPTY,
  filter,
  interval,
  Observable,
  Subscription,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { provideNgxMask } from 'ngx-mask';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../core/environments/environments';
import { materialModules } from './../../core/constants/mat-modules';

import { WaitingListComponent } from './waiting-list/waiting-list.component';
import { ConfirmWaitingListExitDialogComponent } from './confirm-waiting-list-exit-dialog/confirm-waiting-list-exit-dialog.component';
import { QueueComponent } from './queue/queue.component';
import { WelcomeComponent } from './welcome/welcome.component';
import { LanguageSelectorComponent } from '../../shared/components/language-selector/language-selector.component';

import { PortalDataService } from './portal-data.service';
import { WaitingListService } from '../../core/services';
import { Entry, EntryPostBody } from '../../core/interfaces';

const SESSION_STORAGE_ENTRY_KEY = 'entryId';

@Component({
  selector: 'madero-portal',
  templateUrl: './portal.component.html',
  styleUrl: './portal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ...materialModules,
    WaitingListComponent,
    QueueComponent,
    WelcomeComponent,
    LanguageSelectorComponent,
  ],
  providers: [provideNgxMask(), PortalDataService],
})
export class PortalComponent implements OnInit {
  readonly portalDataService = inject(PortalDataService);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #dialog = inject(MatDialog);
  readonly #snackBar = inject(MatSnackBar);
  readonly #waitingListService = inject(WaitingListService);

  readonly #pollingInterval = environment.WAITING_LIST_POLLING_INTERVAL_MS;

  readonly branchId = this.#route.snapshot.paramMap.get('branchId');
  readonly entryId =
    this.#route.snapshot.queryParamMap.get('entryId') ||
    sessionStorage.getItem(SESSION_STORAGE_ENTRY_KEY);

  readonly showWaitingList = signal(false);
  readonly flowError = signal(false);
  readonly entryUncompleted = computed(
    () => this.entry() !== null && this.entry()?.tableGroupId === null
  );

  readonly branch = this.portalDataService.branch;
  readonly entry = this.portalDataService.entry;
  readonly isInsertingEntry = this.portalDataService.isInsertingEntry;
  readonly isLoadingExitQueue = this.portalDataService.isLoadingExitQueue;
  readonly isLoadingBranch = this.portalDataService.isLoadingBranch;
  readonly isLoadingEntry = this.portalDataService.isLoadingEntry;

  ngOnInit(): void {
    this.initializePortal();
  }

  pollingSub: Subscription | undefined = undefined;

  initializePortal(): void {
    this.portalDataService
      .initializeData(this.branchId, this.entryId)
      .pipe(
        tap(() => {
          if (this.entry()) this.startPositionPolling();
        })
      )
      .subscribe({
        error: (err) => {
          console.error(err);
          this.flowError.set(true);
          this.#snackBar.open(err, 'Fechar', {
            verticalPosition: 'top',
            duration: 5000,
          });
        },
      });
  }

  onExitWaitingList(): void {
    this.#dialog
      .open(ConfirmWaitingListExitDialogComponent, {
        width: '100%',
        maxWidth: 300,
      })
      .afterClosed()
      .pipe(
        switchMap((result) =>
          result ? this.#waitingListService.removeEntry(this.entry()!.entryId) : EMPTY
        )
      )
      .subscribe({
        next: () => {
          sessionStorage.removeItem(SESSION_STORAGE_ENTRY_KEY);
          this.showWaitingList.set(false);
          this.stopPositionPolling();
          this.entry.set(null);
          this.#snackBar.open('Você saiu da fila!', 'Fechar', {
            verticalPosition: 'top',
            duration: 5000,
          });
        },
        error: (err) => {
          console.error(err);
          this.#snackBar.open(
            'Opss! Não foi possível sair da fila. Tente novamente em alguns segundos.',
            'Fechar',
            {
              verticalPosition: 'top',
              duration: 5000,
            }
          );
        },
      })
      .add(() => this.isLoadingExitQueue.set(false));
  }

  onJoinedWaitingList(entry: EntryPostBody): void {
    this.isInsertingEntry.set(true);

    this.#waitingListService
      .insertEntry(entry)
      .pipe(
        catchError(({ error }) => {
          const isBranchClosed = error.errors?.find((e: any) => e.code === '114');

          return throwError(() =>
            isBranchClosed
              ? 'Opss! A filial está com a fila fechada no momento.'
              : 'Opss! Não foi possível entrar na fila. Tente novamente em alguns segundos.'
          );
        }),
        tap((res) => {
          this.entry.set(res);
          sessionStorage.setItem(SESSION_STORAGE_ENTRY_KEY, res.entryId);

          this.#snackBar.open('Você entrou na fila com sucesso!', 'Fechar', {
            verticalPosition: 'top',
            duration: 5000,
          });
        }),
        switchMap((res) =>
          this.#waitingListService
            .getEntryPositionData(this.entryId || res.entryId)
            .pipe(
              catchError(() =>
                throwError(
                  () =>
                    'Opss! Não foi possível carregar os dados da fila. Tente novamente em alguns segundos.'
                )
              )
            )
        )
      )
      .subscribe({
        next: (data) => {
          this.isInsertingEntry.set(false);
          this.entry.set(data);
          this.startPositionPolling();
        },
        error: (err) => {
          console.error(err);
          this.#snackBar.open(err, 'Fechar', {
            verticalPosition: 'top',
            duration: 5000,
          });
        },
      })
      .add(() => this.isInsertingEntry.set(false));
  }

  private startPositionGetterPolling(): Observable<Entry> {
    return interval(this.#pollingInterval).pipe(
      filter(() => this.entryUncompleted()),
      switchMap(() => this.#waitingListService.getEntryPositionData(this.entry()!.entryId))
    );
  }

  private handleWaitingListUpdates(currentEntry: Entry): void {
    if (currentEntry) {
      if (this.entry()!.position !== currentEntry.position) {
        this.#snackBar.open('Sua posição na fila foi atualizada!', 'Fechar', {
          verticalPosition: 'top',
          duration: 5000,
        });
      }

      this.entry.set(currentEntry);

      if (!!currentEntry.tableGroupId) {
        sessionStorage.removeItem(SESSION_STORAGE_ENTRY_KEY);
        this.#router.navigate([], {
          relativeTo: this.#route,
          queryParams: { entryId: null },
          queryParamsHandling: 'merge',
        });
      }
    }
  }

  private startPositionPolling() {
    this.pollingSub?.unsubscribe();

    this.pollingSub = this.startPositionGetterPolling().subscribe({
      next: (data) => this.handleWaitingListUpdates(data),
    });
  }

  private stopPositionPolling() {
    this.pollingSub?.unsubscribe();
  }
}
